import { countries, topCountries } from "../data/countries";

export type OptionItem = {
  key: string;
  label: string;
  value: string;
};

export function sortOptionsAlphabetically<T extends any>(
  array: T[],
  sortKey: keyof T,
  order: "asc" | "desc" = "asc",
  locale?: string
): T[] {
  // Multi-language "Other" keywords
  const otherKeywords = ['other', 'otro', 'autre', 'andere', 'outro', 'آخر'];

  // Helper to check if a value is "Other" in any language
  const isOtherOption = (value: string): boolean => {
    const normalizedValue = value.trim().toLowerCase();
    return otherKeywords.some(keyword => normalizedValue === keyword);
  };

  // Helper to check if a value contains "Other" in any language
  const containsOther = (value: string): boolean => {
    const normalizedValue = value.trim().toLowerCase();
    return otherKeywords.some(keyword => normalizedValue.includes(keyword));
  };

  // Separate "Other" options from regular options
  const exactOther = array.filter((item) => isOtherOption(String(item[sortKey])));
  const includesOther = array.filter((item) => {
    const value = String(item[sortKey]);
    return containsOther(value) && !isOtherOption(value);
  });
  const nonOthers = array.filter((item) => !containsOther(String(item[sortKey])));

  // Sort non-Other options alphabetically using locale-aware comparison
  nonOthers.sort((a, b) => {
    const nameA = String(a[sortKey]);
    const nameB = String(b[sortKey]);

    // Use localeCompare for proper language-specific alphabetical sorting
    const comparison = nameA.localeCompare(nameB, locale || 'en', {
      sensitivity: 'base',
      numeric: true
    });

    return order === "asc" ? comparison : -comparison;
  });

  // Sort items that include "other" but aren't exactly "other"
  includesOther.sort((a, b) => {
    const nameA = String(a[sortKey]);
    const nameB = String(b[sortKey]);

    const comparison = nameA.localeCompare(nameB, locale || 'en', {
      sensitivity: 'base',
      numeric: true
    });

    return order === "asc" ? comparison : -comparison;
  });

  // Return with "Other" options at the END (no matter what language)
  return [...nonOthers, ...includesOther, ...exactOther];
}

export const topCountriesId = topCountries
  .map(code => countries.find(country => country.code === code))
  .filter((country): country is any => Boolean(country));

export function sortOptionWithTopValues<T>(array: T[], sortKey: keyof T, topValues: T[]): T[] {
  // Get top options in the given order (without sorting)
  const topOptions = topValues.map((id) => array.find((item) => item[sortKey] === id[sortKey])).filter((item): item is T => Boolean(item)); // Type guard to filter out undefined

  // Get other options and sort them alphabetically by the given sortKey (e.g., "name")
  const otherOptions = array.filter((option) => !topValues.some((value) => value[sortKey] === option[sortKey])).sort((a, b) => String(a[sortKey]).localeCompare(String(b[sortKey])));

  return [...topOptions, ...otherOptions]; // Top values first, then other values alphabetically
}

export const sortByDate = (array: any[], sortKey: keyof any, order: "asc" | "desc" = "asc"): any[] => {
  const copyArray = [...(array || [])];
  const sortedArray = copyArray?.sort?.((a, b) => {
    const dateA = new Date(a?.[sortKey]);
    const dateB = new Date(b?.[sortKey]);
    return order === "asc" ? dateA?.getTime() - dateB?.getTime() : dateB?.getTime() - dateA?.getTime();
  });
  return sortedArray;
};
